import { Providers } from '@microsoft/mgt-element';
import axios from 'axios';
import _ from 'lodash';
import { Lifecycle } from '../../lifecycle/utils/typings';
import { GroupPicker } from '../../templates/CustomComponents/Pickers/GroupPicker';
import { TagsTypeahead } from '../../templates/CustomComponents/Typeahead/TagsTypeahead';
import { parseJwt } from '../../tikit/ticketHelper';
import { RelationType } from '../components/EditTicketRelateTickets/RelateTicketHelper';
import { ILifecycle } from '../interfaces/lifecycle.interface';
import { ITeam } from '../interfaces/supportGroup.interface';
import { TicketTemplate } from '../interfaces/ticket.interface';
import { ACTIVITY_ITEMS, ENTITY_TYPE, LICENSE_FEATURE, MeToken, ScopeTokens, TicketSlaState } from '../utils/constants';
import { getTeamsChannelContext } from '../utils/teamsChannelContext';
import { Guid } from './guid.service';
import { currentPhaseFilter } from '../utils/lifecyclePhaseFilter';

export interface ODataChangeRequest {
  requestUri: string;
  method: string;
  data: string;
}

export class platformService {
  api_url: string | undefined;
  constructor() {
    this.api_url = `${window.__runtimeConfig.platformurl}`;
  }

  static refreshAccessToken = _.debounce(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem(`msal.${window.__runtimeConfig.msalClientId}.idtoken`);
      const jwt = parseJwt(token);

      const now = Math.floor(Date.now() / 1000);
      if (jwt.exp - now <= 600) // If the token is set to expire within 10 minutes, refresh it
        await Providers.globalProvider.getAccessToken();
    } catch { }
  }, 30000, { leading: false, trailing: true });

  static setTenantId = (token) => {
    const jwt = parseJwt(token);
    const tenant = jwt['tid'] || jwt['http://schemas.microsoft.com/identity/claims/tenantid'];
    localStorage.setItem('tid', tenant);
  }

  init = (path: string = 'api/', customHeaders?, customConfig?) => {
    const token = localStorage.getItem('token') || localStorage.getItem(`msal.${window.__runtimeConfig.msalClientId}.idtoken`);
    if (!token) Providers.globalProvider.login();

    const tid = localStorage.getItem('tid');

    let headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'Access-Control-Allow-Origin': '*',
      'x-tikit-tid': tid
    };
    headers = Object.assign(headers, customHeaders);

    let config = {
      baseURL: `${this.api_url}${path}`,
      timeout: 300000,
      headers: headers
    };
    config = Object.assign(config, customConfig);

    // The interceptors retry and page on the client that made the request, so concurrent calls through one service instance cannot cross.
    const client = axios.create(config);

    client.interceptors.response.use(
      response => {
        // Attempt to refresh the token on a successful call, if it falls within the window
        platformService.refreshAccessToken();
        return response;
      },
      async (error: any) => {
        if (error.response && error.response.status && 401 === error.response.status) {
          try {
            await Providers.globalProvider.getAccessToken(null);
            const newToken = localStorage.getItem('token') || localStorage.getItem(`msal.${window.__runtimeConfig.msalClientId}.idtoken`);
            const originalRequest = error.config;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            return client.request(originalRequest);
          } catch {
            // Remove login attempt, and just reject the error if refreshing the token fails.  We should be logging in a different way.
          }
        }

        return Promise.reject(error);
      }
    );

    client.interceptors.response.use(
      async response => {
        if (response.data && response.data['@odata.nextLink']) {
          const url = new URL(response.config.url, response.config.baseURL);
          const skipped = url.searchParams.get('$skip')
          url.searchParams.set("$skip", response.data.value.length + (skipped ? parseInt(skipped, 10) : 0));
          const req = {
            ...response.config,
            url: url.toString()
          };
          const rsp = await client.request(req);
          response.data.value = response.data.value.concat(rsp.data.value);
        }
        return response;
      },
      error => Promise.reject(error)
    );
    return client;
  };

  batch = (batchGuid: string, batchTimeout?: number) => {
    const token = localStorage.getItem('token') || localStorage.getItem(`msal.${window.__runtimeConfig.msalClientId}.idtoken`);
    if (!token) Providers.globalProvider.login();

    const tid = localStorage.getItem('tid');

    let headers = {
      Accept: `multipart/mixed`,
      'Content-Type': `multipart/mixed; boundary=${batchGuid}`,
      Authorization: `Bearer ${token}`,
      'OData-Version': '4.0',
      'OData-MaxVersion': '4.0',
      'x-tikit-tid': tid
    };
    const client = axios.create({
      baseURL: `${this.api_url}api/`,
      timeout: batchTimeout || 31000,
      headers: headers
    });
    client.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error: any) {
        if (401 === error.response.status) {
          Providers.globalProvider.login();
        } else {
          return Promise.reject(error);
        }
      }
    );
    return client;
  };

  updateIsAgentMode = async(isAdminMode:string) => {
    return this.init().post(`AddOrUpdateIsAdminMode`, {IsAdminMode:isAdminMode});
  }

  getUserList = async (orderBy?: string) => {
    const order = orderBy ?? "Id asc"
    const rsp = await this.init().get(`PlatformUser/$count`);
    const total = rsp.data;

    const chunks = Math.ceil(total / 500.0);
    const promises = [];

    for (let i = 0; i < chunks; i++) {
      const skip = i * 500;
      promises.push(this.init().get(`PlatformUser?$skip=${skip}&$top=500&$orderby=${order}`));
    }

    const results = await Promise.all(promises);
    return results.reduce((total, current) => {
      total.push(...current.data.value);
      return total;
    }, []);
  };
  getBulkUsersById = async (userIds: number[]): Promise<PlatformUser[]> => {
    const chunks = userIds.reduce((res, id, idx) => {
      const ci = Math.floor(idx / 100); // Chunks of 100 so we don't blow up the query string
      if (!res[ci])
        res[ci] = [] // start a new chunk

      res[ci].push(id)
      return res;
    }, []);

    const promises = [];
    for (let i = 0; i < chunks.length; i++) {
      promises.push(this.init().get(`PlatformUser?$filter=Id in (${chunks[i].join(',')})`));
      if (i % 4 === 0)
        await Promise.all(promises);
    }

    const results = await Promise.all(promises);
    return results.reduce((total, current) => {
      total.push(...current.data.value)
      return total;
    }, []);
  };

  getTicketList = (params?: string) => {
    return this.init().get(`Ticket${params || ''}`);
  };

  getTicketListFilteredWithCustomProperty = (params?: string) => {
    return this.init().get(`${this.api_url}query/tickets/list${params}`);
  };

  getMyWorkListData = (params?: string) => {
    return this.init().get(`GetMyWork${params || ''}`);
  }

  getAffectedUsers = (params?: string) => {
    return this.init().get(`AffectedUser${params || ''}`);
  };

  getCollaborators = (params?: string) => {
    return this.init().get(`TicketCollaborator${params || ''}`);
  };

  getTagPromise = (params?: string) => {
    return this.init().get(`Tag${params || ''}`);
  };

  getTags = async (params?: string) => {
    try {
      const result = (await this.getTagPromise(params))?.data?.value ?? [];
      result.forEach((tag: any) => {
        tag.header = tag.Name;
        tag.accessibilityitemprops = tag.Id;
        tag.selected = false;
      });
      return result;
    } catch (e) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  };

  getSLAState = (params?: string) => {
    return this.init().get(`SlaState${params || ''}`);
  };

  getUrgency = (params?: string) => {
    return this.init().get(`Urgency${params || ''}`);
  };

  getImpact = (params?: string) => {
    return this.init().get(`Impact${params || ''}`);
  };

  getStatus = (params?: string) => {
    return this.init().get(`Status${params || ''}`);
  };

  getDefaultInstance = (params?: string) => {
    return this.init().get(
      `Status/Action.GetDefaultInstance${params || ''}`
    );
  };

  createStatus = (data: StatusDetail) => {
    return this.init().post(`Status`, data);
  };

  updateStatus = (data: UpdateStatusPayload) => {
    return this.init().patch(`Status(${parseInt(data.Id)})`, data);
  };

  getCategories = (params?: string) => {
    return this.init().get(`Category${params || ''}`);
  };

  getResolutionCategories = (params?: string) => {
    return this.init().get(`ResolutionCategory${params || ''}`);
  };

  getCategoryDefaultInstance = (params?: string) => {
    return this.init().get(
      `Category/Action.GetDefaultInstance${params || ''}`
    );
  };

  createCategory = (data: CategoryDetail) => {
    return this.init().post(`Category`, data);
  };

  updateCategory = (data: UpdateCategoryPayload) => {
    return this.init().patch(`Category(${parseInt(data.Id)})`, data);
  };

  updateResolutionCategory = (data: UpdateCategoryPayload) => {
    return this.init().patch(`ResolutionCategory(${parseInt(data.Id)})`, data);
  };

  createResolutionCategory = (data: CategoryDetail) => {
    return this.init().post(`ResolutionCategory`, data);
  };

  getTicketTypes = (params?: string) => {
    return this.init().get(`TicketType${params || ''}`);
  };
  getTicketTypeSafe = async (id: number, params?: string) => {
    try {
      const result = await this.init().get(`TicketType(${id})${params || ''}`);
      return result.data;
    } catch (e: any) {
      if (e['response']?.status === 402) return null;
      throw e;
    }
  }
  getTicketTypesSafe = async (params?: string) => {
    try {
      const result = await this.init().get(`TicketType${params || ''}`);
      return result.data.value;
    } catch (e: any) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  };

  getTicketTypeDefaultInstance = (params?: string) => {
    return this.init().get(
      `TicketType/Action.GetDefaultInstance${params || ''}`
    );
  };

  createTicketType = (data: TicketTypeDetail) => {
    return this.init().post(`TicketType`, data);
  };

  updateTicketType = (data: UpdateTicketTypePayload) => {
    return this.init().patch(`TicketType(${parseInt(data.Id)})`, data);
  };

  getPriority = (params?: string) => {
    return this.init().get(`Priority${params || ''}`);
  };

  getSource = (params?: string) => {
    return this.init().get(`Source${params || ''}`);
  };

  getResolution = (params?: string) => {
    return this.init().get(`Resolution${params || ''}`);
  };

  getLifecycleStatus = (params?: string) => {
    return this.init().get(`LifecycleStatus${params || ''}`);
  };

  getPriorityDefaultInstance = (params?: string) => {
    return this.init().get(
      `Priority/Action.GetDefaultInstance${params || ''}`
    );
  };

  createPriority = (data: PriorityDetail) => {
    return this.init().post(`Priority`, data);
  };

  updatePriority = (data: UpdatePriorityPayload) => {
    return this.init().patch(`Priority(${parseInt(data.Id)})`, data);
  };

  updateTicket = (id: number | null, data?: {}) => {
    if (id == null) {
      throw new Error('id should not be null');
    } else {
      if (data.hasOwnProperty('TicketLifecycle')) {
        delete data['TicketLifecycle'];
        delete data['TicketLifecycleId'];
      }
      if (data.hasOwnProperty('Template'))
        delete data['Template'];
      if (data.hasOwnProperty('Tags')) {
        delete data['Tags'];
      }
      if (data.hasOwnProperty('Resolution')) {
        delete data["Resolution"].ResolutionCategory;
        if (data["Resolution"].Id != undefined) {
          this.init().patch(`Resolution(${data["Resolution"].Id})`, data["Resolution"]);
          delete data["Resolution"];
        }
      }

      return this.init().patch(`Ticket(${id})`, data);
    }
  };

  updateTicketResolutionNote = (id: number | null, data?: {}) => {
    if (data.hasOwnProperty("ResolutionCategory")) {
      delete data["ResolutionCategory"];
    }

    return this.init().patch(`Resolution(${id})`, data);

  };

  createResolutionNote = (data?: {}) => {
    if (data.hasOwnProperty("ResolutionCategory")) {
      delete data["ResolutionCategory"];
    }
    return this.init().post(`Resolution`, data);
  };



  udpateTicketTags = (tid: number, tags: string) => {
    if (tid == null) {
      throw new Error('id should not be null');
    } else {
      return this.init().post(`Ticket(${tid})/Action.AddTags`, {
        Tags: tags
      });
    }
  };
  attachTicketTags = (ticketId: number, tags: string) => this.init().post(`AddTicketTags`, { TicketId: ticketId, Tags: tags });

  removeRefTags = (id: number, toDeleteTags: string) => {
    return this.init().post(`Ticket(${id})/Action.RemoveTags`, {
      Tags: toDeleteTags
    });
  };

  attachTagsOnTicket = (id: number | null, toRefTags?: number[], isTicketTemplate: boolean = false) => {
    if (id == null) {
      throw new Error('id should not be null');
    } else {
      let body = [];
      const changeSet = `changeset_${Guid.newGuid()}`;
      const batch = `batch_${Guid.newGuid()}`;
      body.push('--' + batch);
      body.push('Content-type: multipart/mixed; boundary=' + changeSet, '');

      let contentId = 1;

      toRefTags?.forEach(tagId => {
        body.push(`--${changeSet}`);
        body.push(`Content-Type: application/http`);
        body.push(`Content-Transfer-Encoding: binary`, '');

        if (!isTicketTemplate)
          body.push(`POST ~/api/Ticket(${id})/Tags/$ref HTTP/1.1`);
        else
          body.push(`POST ~/api/TicketTemplate(${id})/Tags/$ref HTTP/1.1`);

        contentId = this.setBatchBody(body, contentId, tagId);
      });
      body.push('--' + changeSet + '--', '');
      body.push('--' + batch + '--', '');

      let payload = body.join('\r\n');

      return this.batch(batch).post(`$batch`, payload);
    }
  };

  updateTicketAndRefTagsByBatch = (
    id: number | null,
    data?: {},
    toRefTags?: number[]
  ) => {
    if (id == null) {
      throw new Error('id should not be null');
    } else {

      const tagRefStringfiyData = JSON.stringify(data);
      const tagRefData = JSON.parse(tagRefStringfiyData);


      if (tagRefData.hasOwnProperty('Tags')) {
        delete tagRefData['Tags'];
      }

      if (tagRefData.hasOwnProperty('TicketCollaborators'))
        delete tagRefData['TicketCollaborators'];

      let body = [];
      const changeSet = `changeset_${Guid.newGuid()}`;
      const batch = `batch_${Guid.newGuid()}`;
      body.push('--' + batch);
      body.push('Content-type: multipart/mixed; boundary=' + changeSet, '');

      let contentId = 1;
      if (JSON.stringify(tagRefData) != '{}') {
        body.push(`--${changeSet}`);
        body.push(`Content-Type: application/http`);
        body.push(`Content-Transfer-Encoding: binary`, '');
        body.push(`PATCH ~/api/Ticket(${id}) HTTP/1.1`);
        body.push(`Content-ID: 1`);
        body.push(`Accept: application/json;q=0.9, */*;q=0.1`);
        body.push(`Content-Type: application/json;IEEE754Compatible=true`);
        body.push(`OData-Version: 4.0`, '');

        body.push(JSON.stringify(tagRefData));
        contentId = 2;
      }

      toRefTags.forEach(tagId => {
        body.push(`--${changeSet}`);
        body.push(`Content-Type: application/http`);
        body.push(`Content-Transfer-Encoding: binary`, '');
        body.push(`POST ~/api/Ticket(${id})/Tags/$ref HTTP/1.1`);
        contentId = this.setBatchBody(body, contentId, tagId);
      });
      body.push('--' + changeSet + '--', '');
      body.push('--' + batch + '--', '');

      let payload = body.join('\r\n');

      return this.batch(batch).post(`$batch`, payload);
    }
  };

  addComment = (data: { Body: string; IsPublic: boolean }) => {
    return this.init().post(`Comment`, data);
  };

  addCommentToTicket = (
    ticketId: number,
    data: { Body: string; IsPublic: boolean, ModifiedById?: number }
  ) => {
    return this.init().post(`Ticket(${ticketId})/Action.AddComment`, {
      Comment: data
    });
  };

  /** UpdateComment payload - FilesToAdd requires DriveItemId plus one of DriveId/DriveItemGroupId (files must be uploaded first). */
  updateComment = (
    commentId: number,
    data: {
      Comment: { Body: string; IsPublic: boolean; ModifiedById?: number };
      FilesToAdd?: Array<{ FileName: string; MediaType: string; FileSize: number; DriveItemId: string; DriveItemGroupId?: string; DriveId?: string }>;
      FilesToDelete?: number[];
    }
  ) => {
    return this.init().post(`Comment(${commentId})/Action.UpdateComment`, data);
  };

  /** Same as updateComment but also sends notifications to relevant users. */
  updateCommentAndNotify = (
    commentId: number,
    data: {
      Comment: { Body: string; IsPublic: boolean; ModifiedById?: number };
      FilesToAdd?: Array<{ FileName: string; MediaType: string; FileSize: number; DriveItemId: string; DriveItemGroupId?: string; DriveId?: string }>;
      FilesToDelete?: number[];
    }
  ) => {
    return this.init().post(`Comment(${commentId})/Action.UpdateCommentAndNotify`, data);
  };

  attachFilesToComment = async (commentId: number, files: any[]) => {
    try {
      const result = await this.init().post(
        'AttachFilesToComment',
        { 
          CommentId: commentId, 
          Files: files,
        },
      );
      let resultDto = result.data;
      return (resultDto['error']?.length > 0) ? [] : JSON.parse(resultDto['data']);
    } catch (e) {
      return e;
    }
  }

  deleteComment = (commentId: number) => {
    return this.init().post(`Comment(${commentId})/Action.DeleteComment`, {});
  }

  deleteTicket = (id: number | null) => {
    if (id == null) {
      throw new Error('id should not be null');
    } else {
      return this.init().delete(`Ticket(${id})`);
    }
  };

  getCommentsByTicket = (TicketId: number) => {
    return this.init().get(`Ticket(${TicketId})/Comments`);
  };

  getUsersInRole = () => {
    return this.init().get(`GetUsersInRole`);
  };

  GetMyApplicationRoles = () => {
    return this.init().get('GetMyApplicationRoles');
  };

  addTicket = (data: any) => {
    let payload = JSON.parse(JSON.stringify(data));
    if (data.Tags) payload.Tags = payload.Tags.join(',');
    if (data?.hasOwnProperty("AssigneeId") && data?.AssigneeId?.hasOwnProperty("length") && data?.AssigneeId?.length == 0) delete payload.AssigneeId;
    if (payload.AffectedUsers) payload.AffectedUsers = payload.AffectedUsers.map(user=>user.AadObjectId).join(',');
    if (payload.Collaborators) payload.Collaborators = payload.Collaborators.join(',');

    return this.init().post(`AddTicket`, { Ticket: payload });
  };
  addRequest = (data: any) => {
    let payload = JSON.parse(JSON.stringify(data));
    if (data.Tags && data.Tags.length > 0)
      delete payload.Tags;

    return this.init().post(`AddTicket`, { Ticket: payload });
  }
  addRequestEmail = (data: any) => {
    let payload = JSON.parse(JSON.stringify(data));
    return this.init('email').post(`fluentTicket`, payload);
  }

  addTag = (data: any) => {
    return this.init().post(`Tag`, data);
  };

  updateTag = (id: number, data: { Name: string }) => {
    return this.init().patch(`Tag(${id})`, data);
  };

  getPlatformUserId = (data: any) => {
    return this.init().post(`GetPlatformUserId`, data);
  };

  postPlatformUserUser = (user: any) => {
    return this.init().post(`PlatformUser`, user);
  };

  patchPlatformUserUser = (id: any, updatedData: any) => {
    return this.init().patch(`PlatformUser(${id})`, updatedData);
  };

  getPlatformUserByFilter = (filter: any) => {
    return this.init().get(`PlatformUser?$filter=${encodeURI(filter)}`);
  };

  searchPlatformUser = async (user: any) => {
    let filter = '';
    if (user.UserName && user.UserName.length > 0)
      filter = `UserName eq '${user.UserName}'`;
    if (user.AadObjectId && user.AadObjectId.length > 0)
      filter += `${(filter.length > 0 ? ' or ' : '')}AadObjectId eq '${user.AadObjectId}'`;

    const platformUser = (await this.getPlatformUserByFilter(filter)).data.value;
    return platformUser?.[0];
  }
  getOrCreatePlatformUser = async (user: any) => {
    if (user.UserName==MeToken){
      return ScopeTokens.MeTokenPlatformUser;
    }
    else
    {
      let platformUser = await this.searchPlatformUser(user);

      if (platformUser) {
        if (platformUser.AadObjectId != null && platformUser.AadObjectId != user.AadObjectId) {
          await this.patchPlatformUserUser(platformUser.Id, { AadObjectId: user.AadObjectId })

          platformUser = (await this.getPlatformUser(platformUser.Id)).data;
          delete platformUser['@odata.context'];
        }

          return platformUser;
      }
    }

    return (
      await this.postPlatformUserUser({
        Email: user.Email,
        GivenName: user.GivenName,
        FamilyName: user.FamilyName,
        UserName: user.UserName,
        AadObjectId: user.AadObjectId
      })
    ).data;
  };

  sendToConversation = async (data: any) => {
    return this.init().post('SendToConversation', data);
  };

  sendCustomFormToConversation = async (data: any) => {
    return this.init().post('SendCustomFormToConversation', data);
  };

  sendToTriageChannel = async (data: any) => {
    return this.init().post('SendToTriageChannel', data);
  };

  exportTicketsToCsv = async (filter: string) => {
    let fileResponse = await this.init().get(`${this.api_url}export/tickets/csv?${filter}`, { responseType: 'blob' });
    let fileName = new Date(Date.now()).toLocaleString().replace(/[\/:,\s]/g, '');
    this.setupDownloadLink(`tickets-${fileName}.csv`, fileResponse);
  }

  getAiFile = async (container: string, fileName: string) => {
    let response = await this.init().get(`${this.api_url}getaifile?cn=${container}&fn=${fileName}`, { responseType: 'blob' });
    this.setupDownloadLink(fileName, response);
  }

  getTicketAttachments = async (ticketId, commentId) => {
    let response = await this.init().get(`${this.api_url}ticket/downloadTicketFiles/${ticketId}${(!commentId ? '' : `?commentId=${commentId}`)}`, { responseType: 'blob' });
    this.setupDownloadLink(`Tikit_${ticketId}_${Date.now()}.zip`, response);
  }

  setupDownloadLink = (fileName: string, response: any) => {
    let url = window.URL.createObjectURL(new Blob([response.data]));
    let link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  getSystemSetting = (type: string) =>
    this.init().get(`Get_SystemSetting(TypeName='${type}')`);

  setSystemSetting = (type: string, data: any) => {
    const payload = {
      SystemSettings: {
        '@odata.type': `#${type}`,
        ...data
      }
    };
    return this.init().post(`Set_SystemSetting`, payload);
  };

  getGroupChatEnabled = () => this.init().get(`GroupChatEnabled`);
  groupChatFeatureEnabled = () => this.init().get(`HasFeature(FeatureName='${LICENSE_FEATURE.GroupChat}')`);

  getTeamsBotConfiguration = () => this.init().get(`GetTeamsBotConfiguration`);
  getEmailConnectorConfiguration = () => this.init().get(`GetEmailConnectorConfiguration`);
  getDefaultTeamUnrestricted = () => this.init().get(`GetDefaultTeamUnrestricted`);
  getCultures = () => this.init().get('GetCultures');

  hasFeature = (feature: string) => {
    if (feature == LICENSE_FEATURE.GroupChat)
      return this.init().get(`GroupChatEnabled`);
    else
      return this.init().get(`HasFeature(FeatureName='${feature}')`)
  };
  getAvailableFeatures = () => this.init().get(`GetAvailableFeatures`);

  getLicenseFeatures = () => this.init().get(`GetAllLicenseFeatures`);
  getLicenseProducts = () => this.init().get(`GetAllLicenseProducts`);
  getTenantLicenses = () => this.init().get(`GetTenantLicenses`);

  getHelpCardScopedButtons = () => {
    return this.init().get(`HelpCardScopedInstance?$expand=ScopedMembers`);
  }

  installHelpBot = (data: any) => {
    return this.init().post(`InstallHelpBot`, data);
  };

  getGraphAccessToken = () => {
    return this.init().get(`graph-access-token`);
  };

  getTicket = (id: number | null, params?: string) => {
    if (id == null) {
      throw new Error('id should not be null');
    } else {
      return this.init().get(`Ticket(${id})${params || ''}`);
    }
  };

  getPlatformUser = (id: number | null, params?: string) => {
    if (id == null) {
      throw new Error('id should not be null');
    } else {
      return this.init().get(`PlatformUser(${id})${params || ''}`);
    }
  };

  getCardPayload = (
    id: number | null,
    bannerMessage: string = '',
    isTriage: boolean = false
  ) => {
    return this.init().get(
      `GetTicketAdaptiveCard(Id=${id},UserDate=${Date.now()},Message='${encodeURI(
        bannerMessage
      )}',IsTriage=${isTriage})`
    );
  };

  getSupportGroups = (params?: string) => {
    return this.init().get(`SupportGroup${params || ''}`);
  };

  updateSupportGroup = (type: string, id?: number | null, data?: {}) => {
    if (id === null || id === 0) {
      this.setSupportGroup(type, data);
    }

    return this.init().patch(`SupportGroup(${id})`, data);
  };

  updateSupportGroupMemmbers = (
    supportGroupId: number,
    memberIds: string[],
    name: string,
    description: string,
    channelId: string,
    teamsAadObjectId: string
  ) => {
    return this.init().post('UpdateSupportGroup', {
      SupportGroupId: supportGroupId,
      MemberAadObjectIds: memberIds,
      Name: name,
      Description: description,
      TeamsChannelId: channelId,
      TeamsAadObjectId: teamsAadObjectId
    });
  };

  setSupportGroup = (type: string, data: any) => {
    this.getSupportGroups(data.Id).then(response => {
      if (response.data.Id !== null && response.data.Id >= 0) return response;
    });

    const payload = {
      '@odata.type': `#${type}`,
      ...data
    };

    return this.init().post(`SupportGroup`, payload);
  };

  getRoleByName = (params: string) => {
    // Intentionally requires params
    return this.init().get(`KnownProviderRole${params}`);
  };

  syncUser = (userId: string) => {
    return this.init().get(`SyncUser(UserId=${userId})`);
  };

  syncAllUsers = () => {
    return this.init().get(`SyncAllUsers`);
  };

  addOrRemoveUserLicense = (userId: number, isConsumed: boolean) => {
    return this.init().get(`AddOrRemoveUserLicense(UserId=${userId},AddLicense=${!isConsumed})`);
  };

  updateUsersLicenses = (data: any) => {
    return this.init().post(`UpdateUserLicenses`, data);
  };

  getUser = (userId: string, params?: string) => {
    return this.init().get(`PlatformUser(${userId})${params || ''}`);
  };

  getUsers = (params?: string) => {
    if (params)
      return this.init().get(`PlatformUser${params || ''}`);

    return this.init().get(`PlatformUser?$orderby=FullName`);
  };

  getUserLicenses = (params?: string) => {
    return this.init().get(`UserProductLicense${params || ''}`);
  };

  createTag = (data: { Name: string }) => {
    return this.init().post('Tag', data);
  };

  deleteTag = (id: number) => {
    return this.init().delete(`Tag(${id})`);
  };

  //Ticket Actions

  takeTicket = (id: number | null) => {
    if (id == null) {
      throw new Error('id should not be null');
    } else {
      return this.init().post(`Ticket(${id})/Action.TakeTicket`);
    }
  };

  getTicketActivities = (
    entityId: number,
    top: number,
    skip: number,
    commentOnly: boolean,
    filter: string
  ) => {
    return this.init().get(
      `Ticket(${entityId})/Action.GetActivities?$top=${top}&$skip=${skip}&commentOnly=${commentOnly}&$filter=${filter}`
    );
  };

  getAllTicketActivities = (
    entityId: number,
    skip: number = 0,
  ) => {
    return this.init().get(
      `Ticket(${entityId})/Action.GetActivities?$top=${ACTIVITY_ITEMS}&$skip=${skip * ACTIVITY_ITEMS}&$orderby=ModifiedDate`
    );
  };

  getAllTicketActivityCount = (
    entityId: number,
  ) => {
    return this.init().get(
      `Ticket(${entityId})/Action.GetActivities/$count`
    );
  }

  meAuthenticate = () => this.init().get('meauthenticate');
  me = () => this.init().get('me');

  getSubscription = () => this.init('customer/').get('get-subscription');
  checkSubscription = () => this.init('customer/').get('check-subscription');
  getSubscriptionSchedule = () => this.init('customer/').get('get-subscription-schedule');
  getLicensedAnalyst = () => this.init('customer/').get('get-licensed-analyst');

  getSubscriptionType = () => this.init().get('GetSubscriptionType');
  getLicenseSettings = () => this.init().get('GetLicenseSettings');
  getCheckoutLink = (successUri: string, cancelUri: string) =>
    this.init().post(`CreateCheckoutSession`, {
      SuccessUri: successUri,
      CancelUri: cancelUri
    });
  getPortalLink = (returnUri: string) => this.init().post(`CreatePortalSession`, { ReturnUri: returnUri });
  retryPayment = () => this.init().post(`RetryPayment`, {});
  cancelSubscription = () => this.init().post('CancelSubscription');
  updateSubscription = (
    customerId: string,
    paymentMethodId: string,
    priceId: string,
    quantity: number,
    address: any,
    email: string
  ) =>
    this.init('customer/').post('update-subscription', {
      paymentMethodId: paymentMethodId,
      customerId: customerId,
      priceId: priceId,
      quantity: quantity,
      email: email,
      address: address,
      emailOnly: priceId === ''
    });
  getInvoicePreview = (seatCount: number) => this.init('customer/').post('invoice-preview', { seatCount: seatCount });
  getLatestInvoice = (invoiceId: string) => this.init('customer/').post('latest-invoice', { invoiceId: invoiceId });
  /** Automations **/
  getAutomationList = (params?: string) => this.init().get(`Phase${params || ''}`);
  getAutomation = (phaseId: number) => this.init().get(`Phase(${phaseId})?$expand=Trigger,Steps($expand=Step)&$select=Id,Title,Status,Description,Enabled,TriggerId,Trigger,Steps`);
  getRuleStep = (stepId: number) => this.init().get(`Rule(${stepId})?$expand=EntitySections($expand=Groups($expand=ChildrenGroups, RuleCriteria))`);
  getEnumValues = (typeFullName: string) => this.init().get(`GetEnumByType(TypeFullName='${typeFullName}')`);
  getAutomationProperties = (entityName: string = ENTITY_TYPE.Ticket) => this.init().get(`GetAutomationProperties(EntityName='${entityName}')`);
  getEntityProperties = (entityName: string = ENTITY_TYPE.Ticket) => this.init().get(`GetEntityProperties(EntityName='${entityName}')`);
  getTriggerApplicableTypes = () => this.init().get('GetTriggerApplicableTypes');
  getGlobalRules = () => this.init().get('Rule?$filter=(true and (IsGlobal eq true))');
  getGlobalActions = () => this.init().get('Action?$filter=(true and (IsGlobal eq true))');
  deleteAutomation = (id: number) => this.init().delete(`Phase(${id})`);
  toggleAutomation = (id: number, data: ToggleAutomationPayload) => this.init().patch(`Phase(${id})`, data);
  getPropertyValuePair = (id: number) => this.init().get(`Action(${id})/PropertyValuePair`);
  getActionNotification = (id: number) => this.init().get(`Action(${id})/ActionNotification?$expand=NotifyUsers($select=Id,UserName,AadObjectId,FullName)`);
  getActionApplyTemplate = (id: number) => this.init().get(`Action(${id})/ActionApplyTemplate?$expand=NotifyUser($select=Id,UserName,AadObjectId,FullName)`);
  getActionSendForm = (id: number) => this.init().get(`Action(${id})/ActionSendForm?$expand=NotifyUser($select=Id,UserName,AadObjectId,FullName)`);
  getEntitySections = (id: number) => this.init().get(`Rule(${id})/EntitySections`);
  getRuleEntitySectionGroups = (id: number) => this.init().get(`RuleEntitySection(${id})/Groups`);
  getRuleGroupChildrenGroups = (id: number) => this.init().get(`RuleGroup(${id})/ChildrenGroups`);
  getRuleGroupRuleCriteria = (id: number) => this.init().get(`RuleGroup(${id})/RuleCriteria`);
  createUpdateAutomation = (payload: AutomationCreatePayload) => this.init().post('SaveAutomationFlow', { Phase: JSON.stringify(payload) });

  getTicketFiles = async (ticketId: number) => {
    try {
      const result = await this.init().get(
        `Ticket(${ticketId})/Action.GetTicketAttachments`
      );
      return result.data.value;
    } catch {
      return [];
    }
  };

  // Stored thumbnail links are signed SharePoint URLs that expire. Called only when a preview
  // actually fails to load, so nothing is spent on images that are still fine.
  refreshAttachmentThumbnail = async (attachmentId: number): Promise<string> => {
    try {
      const result = await this.init().post('RefreshAttachmentThumbnail', { AttachmentId: attachmentId });
      return result?.data?.thumbnailUrl ?? '';
    } catch {
      return '';
    }
  };

  updateCustomFormAnswers = (
    ticketId: number,
    data: string
  ) => {
    return this.init().post(`Ticket(${ticketId})/Action.UpdateCustomFormAnswers`, {
      CardAnswerJson: data
    });
  };

  uploadTicketFiles = (ticketId: number, files: any[], message: string, isPrivate: boolean, isEndUserPage: boolean, sendToChat?: boolean, CardAnswerJson?: string) => {
    let formData = new FormData();
    formData.append('ticketId', ticketId.toString());
    formData.append('message', message);
    formData.append('isPrivate', isPrivate ? 'true' : 'false');
    formData.append('isEndUserPage', isEndUserPage ? 'true' : 'false');
    formData.append('sendToChat', sendToChat ? 'true' : 'false');
    formData.append('CardAnswerJson', CardAnswerJson?.toString() ?? "");
    // The other upload path. Same reason as getUploadUrl: this posts from a Teams dialog that knows its
    // channel, and without these the destination falls back to the team's configured channel.
    const { teamsTeamId, teamsChannelId } = getTeamsChannelContext();
    if (teamsTeamId) formData.append('teamsTeamId', teamsTeamId);
    if (teamsChannelId) formData.append('teamsChannelId', teamsChannelId);
    files.forEach(item => {
      formData.append(item.name, item.file ?? item);
    });
    return this.init('api/', { 'Content-Type': 'multipart/form-data' }, { timeout: 0 })
      .post('UploadTicketFiles', formData)
      .then(result => {
        let resultDto = result.data;
        if (resultDto['error'].length > 0) return [];
        else return JSON.parse(resultDto['data']);
      })
      .catch((e) => {
        return e;
      });
  };
  attachTicketFiles = async (ticketId: number | string, files: any[], message: string, isPrivate: boolean, isEndUserPage: boolean, sendToChat?: boolean) => {
    try {
      let ticket = ticketId;
      if (typeof ticketId === "string")
        ticket = parseInt(ticketId, 10);

      const result = await this.init().post('AttachFilesToTicket', { TicketId: ticket, Files: files, Message: message, IsPrivate: isPrivate, IsEndUser: isEndUserPage, SendToChat: sendToChat });
      let resultDto = result.data;
      return (resultDto['error'].length > 0) ? [] : JSON.parse(resultDto['data']);
    } catch (e) {
      return e;
    }
  };
  attachWebCustomFormFiles = async (ticketId: number | string, customForms: any[], formFiles: any[], isEndUserPage: boolean, sendToChat?: boolean) => {
    try {
      let ticket = ticketId;
      if (typeof ticketId === "string")
        ticket = parseInt(ticketId, 10);

      const result = await this.init().post('AttachFilesFromWebCustomForm', { TicketId: ticket, CustomForms: customForms, FormFiles: formFiles, IsEndUser: isEndUserPage, SendToChat: sendToChat });
      let resultDto = result.data;
      return (resultDto['error']?.length > 0) ? [] : JSON.parse(resultDto['data']);
    } catch (e) {
      return e;
    }
  };

  attachBotCustomFormFiles = async (ticketId: number | string, templateId: number | string, files: any[], message: string, isPrivate: boolean, cfData: string) => {
    try {
      let ticket = ticketId;
      if (typeof ticketId === "string")
        ticket = parseInt(ticketId, 10);

      let template = templateId;
      if (typeof templateId === "string")
        template = parseInt(templateId, 10);

      const result = await this.init().post('AttachFilesFromBotCustomForm', { TicketId: ticket, TemplateId: template, Files: files, Message: message, IsPrivate: isPrivate, CustomFormData: cfData });
      let resultDto = result.data;
      return (resultDto['error'].length > 0) ? [] : JSON.parse(resultDto['data']);
    } catch (e) {
      return e;
    }
  };

  deleteFile = (id: number) => {
    return this.init().delete(`FileAttachment(${id})`);
  };

  getTicketTemplates = (params?: string) => {
    return this.init().get(`TicketTemplate${params || ''}`);
  };

  getScopedErrorMessageCard = (msgType?: string) => {

    return this.init().post(`GetScopedErrorMessageCard`, {MsgType: msgType ?? "Template"});
  };

  getUnScopedTicketTemplates = () => {
    return this.init().get(`GetTemplates`);
  };

  createTicketTemplate = (data: TicketTemplate) => {
    let payload = JSON.parse(JSON.stringify(data));
    if (data.Tags && data.Tags.length > 0) delete payload.Tags;
    if (data.AffectedUsers && data.AffectedUsers.length > 0) delete payload.AffectedUsers;
    if (data.Collaborators && data.Collaborators.length > 0) delete payload.Collaborators;

    return this.init().post('TicketTemplate', payload);
  };

  attachTemplateUsers = async (templateId: number, affected: number[], collabs: number[]): Promise<any> => {
    if (templateId == null) throw new Error('id should not be null');

    let body = [];
    const changeSet = `changeset_${Guid.newGuid()}`;
    const batch = `batch_${Guid.newGuid()}`;
    body.push('--' + batch);
    body.push('Content-type: multipart/mixed; boundary=' + changeSet, '');

    let contentId = 1;

    affected?.forEach(id => {
      body.push(`--${changeSet}`);
      body.push(`Content-Type: application/http`);
      body.push(`Content-Transfer-Encoding: binary`, '');

      body.push(`POST ~/api/TicketTemplate(${templateId})/AffectedUsers/$ref HTTP/1.1`);

      contentId = this.setBatchBody(body, contentId, id);
    });
    collabs?.forEach(id => {
      body.push(`--${changeSet}`);
      body.push(`Content-Type: application/http`);
      body.push(`Content-Transfer-Encoding: binary`, '');

      body.push(`POST ~/api/TicketTemplate(${templateId})/Collaborators/$ref HTTP/1.1`);

      contentId = this.setBatchBody(body, contentId, id);
    });

    body.push('--' + changeSet + '--', '');
    body.push('--' + batch + '--', '');

    let payload = body.join('\r\n');

    return this.batch(batch).post(`$batch`, payload);
  }

  updateTicketTemplate = (data: TicketTemplate) => {
    let payload = JSON.parse(JSON.stringify(data));
    delete payload["@odata.context"];

    let newData: any = {
      Tags: data.Tags,
      AffectedUsers: data.AffectedUsers?.map(u => u.Id) ?? [],
      Collaborators: data.Collaborators?.map(u => u.Id) ?? []
    };
    delete payload["Tags"];
    delete payload["AffectedUsers"];
    delete payload["Collaborators"];
    delete payload["TemplateForms"];

    newData.TicketTemplate = payload;

    return this.init().post(`CommitTemplate?skipScoping=true`, newData);
  };

  deleteTicketTemplate = (id: number) => {
    return this.init().delete(`TicketTemplate(${id})`);
  };

  getApplicationRoles = () =>
    this.init().get('ApplicationRoles(IncludeAllCategories=true)');

  deleteMappedRole = (payload: MappedRolePayload) =>
    this.init().post('RemoveApplicationRoleMap', payload);

  batchSetPermission = () => this.init().post('BatchSetPermissions', { Batch: [] });
  resetClaims = () => this.init().post('ResetClaims', {});
  resetUserClaims = () => this.init().post('ResetUserClaims', {});

  getTemplatesTicketCount = () => this.init().get('Ticket?$apply=groupby((TemplateId), aggregate($count as Count))');
  getTemplateCountByTicket = async () => {
    const { data: { value } } = await this.init().get('Ticket?$select=TemplateId&$filter=TemplateId ne null');
    const result = [];
    value.forEach((el: any) => {
      let idx = result.findIndex(x => x.TemplateId == el.TemplateId);
      if (idx === -1) result.push({ TemplateId: el.TemplateId, Count: 1 });
      else result[idx].Count++;
    });

    return result;
  };

  getAccessTokens = () => {
    return this.init().get(`RegisteredApiConnectionToken?$expand=Owner`);
  };

  getAccessToken = (id: string) => {
    return this.init().get(`RegisteredApiConnectionToken(${id})`);
  };

  deleteToken = (data: any) => {
    return this.init().post(`RemoveRemoteServiceToken`, data);
  };

  createToken = (data: any) => {
    return this.init().post(`CreateRemoteServiceToken`, data);
  };

  getMyApplicationRoleNames = async () => {
    let myApplicationRoles = (await this.init().get('GetMyApplicationRoles'))
      .data.value;
    return _.uniq(
      _.map(myApplicationRoles, function (r) {
        return r.RoleName;
      })
    );
  };


  getProviderRoleToApplicationRoleMap = async (params) => {
    return await this.init().get(`ProviderRoleToApplicationRoleMap${params || ''}`);
  }


  createMappedRole = async (data: MappedRolePayload[]) => {

    const body = [];
    const changeSet = `changeset_${Guid.newGuid()}`;
    const batch = `batch_${Guid.newGuid()}`;

    body.push('--' + batch);
    body.push('Content-type: multipart/mixed; boundary=' + changeSet, '');

    body.push(`--${changeSet}`);
    body.push(`Content-Type: application/http`);
    body.push(`Content-Transfer-Encoding: binary`, '');

    let i = 0;
    for (i; i < data.length; i++) {

      if (data[i].ApplicationRole == "Superagents")
        data[i].ApplicationRole = "Analysts";
      else if (data[i].ApplicationRole == "Servicecatalogadmins")
        data[i].ApplicationRole = "KnowledgeAgents";
      else if (data[i].ApplicationRole == "Servicecatalogreaders")
        data[i].ApplicationRole = "KnowledgeReader";

      body.push('POST ~/api/CreateApplicationRoleMap HTTP/1.1');
      body.push(`Content-ID: ${i + 1}`);
      body.push(`Accept: application/json;q=0.9, */*;q=0.1`);
      body.push(`Content-Type: application/json;IEEE754Compatible=true`);
      body.push(`OData-Version: 4.0`);
      body.push(`OData-MaxVersion: 4.0`, '');

      body.push(JSON.stringify(data[i]));
      body.push(`--${changeSet}`);
      body.push(`Content-Type: application/http`);
      body.push(`Content-Transfer-Encoding: binary`, '');
    }

    body.push('POST ~/api/BatchSetPermissions HTTP/1.1');
    body.push(`Content-ID: ${i + 1}`);
    body.push(`Accept: application/json;q=0.9, */*;q=0.1`);
    body.push(`Content-Type: application/json;IEEE754Compatible=true`);
    body.push(`OData-Version: 4.0`);
    body.push(`OData-MaxVersion: 4.0`, '');

    body.push(JSON.stringify({ Batch: [] }));

    body.push(`--${changeSet}`);
    body.push(`Content-Type: application/http`);
    body.push(`Content-Transfer-Encoding: binary`, '');

    body.push('POST ~/api/ResetClaims HTTP/1.1');
    body.push(`Content-ID: ${i + 2}`);
    body.push(`Accept: application/json;q=0.9, */*;q=0.1`);
    body.push(`Content-Type: application/json;IEEE754Compatible=true`);
    body.push(`OData-Version: 4.0`);
    body.push(`OData-MaxVersion: 4.0`, '');

    body.push(JSON.stringify({ }));

    body.push('--' + changeSet + '--', '');
    body.push('--' + batch + '--', '');

    let payload = body.join('\r\n');

    return this.batch(batch).post(`$batch`, payload);
  };

  updatePickerPositions = (
    url: string,
    data: UpdatePickerPositionPayload[]
  ) => {
    const body = [];
    const changeSet = `changeset_${Guid.newGuid()}`;
    const batch = `batch_${Guid.newGuid()}`;

    body.push('--' + batch);
    body.push('Content-type: multipart/mixed; boundary=' + changeSet, '');

    body.push(`--${changeSet}`);
    body.push(`Content-Type: application/http`);
    body.push(`Content-Transfer-Encoding: binary`, '');

    let i = 0;
    for (i; i < data.length; i++) {
      body.push(`PATCH ~/api/${url}(${data[i].Id}) HTTP/1.1`);
      body.push(`Content-ID: ${i + 1}`);
      body.push(`Accept: application/json;q=0.9, */*;q=0.1`);
      body.push(`Content-Type: application/json;IEEE754Compatible=true`);
      body.push(`OData-Version: 4.0`);
      body.push(`OData-MaxVersion: 4.0`, '');

      body.push(JSON.stringify(data[i]));
      if (i < data.length - 1) {
        body.push(`--${changeSet}`);
        body.push(`Content-Type: application/http`);
        body.push(`Content-Transfer-Encoding: binary`, '');
      }
    }

    body.push('--' + changeSet + '--', '');
    body.push('--' + batch + '--', '');

    let payload = body.join('\r\n');

    return this.batch(batch).post(`$batch`, payload);
  };

  updateBulkTickets = (data: BulkTicketEntity[], tagsRef: number[]) => {
    const body = [];
    const changeSet = `changeset_${Guid.newGuid()}`;
    const batch = `batch_${Guid.newGuid()}`;

    body.push('--' + batch);
    body.push('Content-type: multipart/mixed; boundary=' + changeSet, '');

    let i = 0;
    let contentId = 0;
    for (i; i < data.length; i++) {
      contentId = contentId + 1;
      body.push(`--${changeSet}`);
      body.push(`Content-Type: application/http`);
      body.push(`Content-Transfer-Encoding: binary`, '');
      body.push(`PATCH ~/api/Ticket(${data[i].Id}) HTTP/1.1`);
      body.push(`Content-ID: ${contentId}`);
      body.push(`Accept: application/json;q=0.9, */*;q=0.1`);
      body.push(`Content-Type: application/json;IEEE754Compatible=true`);
      body.push(`OData-Version: 4.0`, '');

      if (data[i].hasOwnProperty('Tags')) {
        delete data[i]['Tags'];
      }
      body.push(JSON.stringify(data[i]));

      if (tagsRef) {
        tagsRef.forEach(tagId => {
          contentId = contentId + 1;
          body.push(`--${changeSet}`);
          body.push(`Content-Type: application/http`);
          body.push(`Content-Transfer-Encoding: binary`, '');
          body.push(`POST ~/api/Ticket(${data[i].Id})/Tags/$ref HTTP/1.1`);
          body.push(`Content-ID: ${contentId}`);
          body.push(`Accept: application/json;q=0.9, */*;q=0.1`);
          body.push(`Content-Type: application/json;IEEE754Compatible=true`);
          body.push(`OData-Version: 4.0`);
          body.push(`OData-MaxVersion: 4.0`, '');
          body.push(`{"@odata.id":"http://local/api/Tag(${tagId})"}`);
        });
      }
    }

    body.push('--' + changeSet + '--', '');
    body.push('--' + batch + '--', '');

    let payload = body.join('\r\n');

    const timeout = (data.length > 2) ? (data.length / 2) * 40000 : 40000;
    return this.batch(batch, timeout).post(`$batch`, payload);
  };

  batchDeleteTickets = (ids: number[]) => {
    const changeSet = `changeset_${Guid.newGuid()}`;
    const batch = `batch_${Guid.newGuid()}`;
    const body = [];

    body.push('--' + batch);
    body.push('Content-type: multipart/mixed; boundary=' + changeSet, '');

    ids.forEach((id, i) => {
      body.push(`--${changeSet}`);
      body.push(`Content-Type: application/http`);
      body.push(`Content-Transfer-Encoding: binary`, '');
      body.push(`DELETE ~/api/Ticket(${id}) HTTP/1.1`);
      body.push(`Content-ID: ${i + 1}`);
      body.push(`Accept: application/json;q=0.9, */*;q=0.1`);
      body.push(`OData-Version: 4.0`, '');
      body.push('');
    });

    body.push('--' + changeSet + '--', '');
    body.push('--' + batch + '--', '');

    const payload = body.join('\r\n');
    const timeout = ids.length > 2 ? ids.length * 10000 : 20000;
    return this.batch(batch, timeout).post(`$batch`, payload);
  };

  getMe = async (params?: string) => {
    return this.init().get(`me${params || ''}`);
  };

  relateTickets = async (data: any) => {
    return this.init().post('RelateTickets', data);
  };

  getRelatedTicketsIncludeDeleted = async (sourceTicketId: number) => {
    const result = await this.init().get(
      `Ticket(${sourceTicketId})/RelatedTickets`
    );
    return result.data.value;
  };

  getRelatedTickets = async (sourceTicketId: number) => {
    const result = await this.init().get(`RelatedTickets?$filter=IsDeleted%20eq%20false%20and%20SourceTicketId eq ${sourceTicketId} or RelatedTicketId eq ${sourceTicketId} 
    &$expand=relatedticket($select=Id,Title,StatusId),SourceTicket($select=Id,Title,StatusId) &$select=Id,RelationshipTypeId,CreatedById,CreatedDate&$orderby=Id desc`);
    return result.data.value;
  };

  getMergedSourceTicket = async (mergedTicketId: number) => {
    const result = await this.init()
      .get(`RelatedTickets?$filter=RelatedTicketId eq ${mergedTicketId} 
    and RelationshipTypeId eq ${RelationType.MergeTickets}`);
    return result.data.value[0];
  };

  getAllMergedTickets = async () => {
    try {
      const result = await this.init().get(
        `RelatedTickets?$filter=RelationshipTypeId eq ${RelationType.MergeTickets}`
      );
      return result.data.value;
    } catch {
      return [];
    }
  };

  getTicketComments = async (ticketIds: string) => {
    try {
      const result = await this.init().get(`Ticket?$filter=Id%20in%20(${ticketIds})&$expand=Comments($select=Body)&$select=Id`);
      return result.data.value;
    } catch {
      return [];
    }
  }
  getTicketCommentCount = async (ticketId: number) => {
    try {
      const result = await this.init().get(`Ticket(${ticketId})/Comments/$count`);
      return result.data;
    } catch {
      return 0;
    }
  }

  getSlaProperties = (entityName: string = ENTITY_TYPE.Ticket) =>
    this.init().get(`GetSlaProperties(EntityName='${entityName}')`);

  createSla = (body: SlaCreate) => {
    return this.init().post('CreateServiceLevelAgreement', body);
  };

  getSlaList = (params?: string) => this.init().get(`ServiceLevelAgreement${params || ''}`);

  getSla = (id: number, params?: string) => this.init().get(`ServiceLevelAgreement(${id})${params || ''}`);

  toggleSla = (id: number, isActive: boolean) => this.init().patch(`ServiceLevelAgreement(${id})`, { IsActive: isActive });

  deleteSla = (id: number) => this.init().delete(`ServiceLevelAgreement(${id})`);

  getTicketSla = async (_params?: string) => {
    try {
      const { data: { value } } = await this.init().get(`TicketSla${_params ? _params : ''}`);
      return value;
    } catch (err: any) {
      if (err['response']?.status === 402) return [];
      throw err;
    }
  };
  getTicketSlaCountBySla = async () => {
    const { data: { value } } = await this.init().get('TicketSla?$select=ServiceLevelAgreementId,TicketId&$expand=SlaState($select=Name)');
    const result = [];
    value.forEach((el: any) => {
      let idx = result.findIndex(x => x.SlaId == el.ServiceLevelAgreementId);
      if (idx === -1)
        result.push({ SlaId: el.ServiceLevelAgreementId, TicketCount: 1, BreachedCount: el.SlaState.Name == TicketSlaState.Breached ? 1 : 0 });
      else {
        result[idx].TicketCount++;
        result[idx].BreachedCount += el.SlaState.Name == TicketSlaState.Breached ? 1 : 0
      }
    });

    return result;
  };

  getCustomViewsSafe = async (_params: string = '?$expand=ViewColumns(select=ColumnKey,Order),ViewFilters(select=Id,FilterKey,FilterValues,NumberValue,StringValue),ScopedMembers(select=Member,AadObjectId,ScopedType,CreatedById)') => {
    try {
      const result = await this.init().get(`CustomView${_params ? _params : ''}`);
      return result?.data?.value || [];
    } catch (e) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  };

  addOrUpdateHelpCardButtonScope = (data: any[]) => {
    return this.init().post(`AddOrUpdateHelpCardButtons`, {ButtonDetailList: data});
  };

  createCustomView = (_body: CreateCustomView) => this.init().post('CustomView', _body);

  addOrUpdateCustomView = (customView) => {
    return this.init()
      .post('AddOrUpdateCustomView', customView)
      .then((result) => {
        const { IsError, Data } = result.data;

        if (IsError) {
          return null
        }

        return JSON.parse(Data)
      })
      .catch(() => {
        return null
      });
  }

  deleteCustomView = (viewId: number) => {
    return this.init().delete(`CustomView(${viewId})`);
  };

  getTeams = (params?: string) => {
    return this.init().get(`Team${params || ''}`);
  };

  getTeamSafe = async (id: number, params?: string) => {
    try {
      const result = await this.init().get(`Team(${id})${params || ''}`);
      return result.data;
    } catch (e: any) {
      if (e['response']?.status === 402) return null;
      throw e;
    }
  };
  getTeamsSafe = async (params?: string) => {
    try {
      const result = await this.init().get(`Team${params || ''}`);
      return result.data.value;
    } catch (e: any) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  };

  UpdateAffectedUsers = (id: number, affectedUserIds: any) => {
    return this.init().post(`Ticket(${id})/Action.UpdateAffectedUsers`, { AffectedUserIds: affectedUserIds });
  };
  UpdateTicketAffectedUsers = (id: number, affectedUserIds: any) => this.init().post(`UpdateTicketAffectedUsers`, { TicketId: id, AffectedUserIds: affectedUserIds });

  UpdateCollaborators = (id: number, collaboratorIds: any) => {
    return this.init().post(`Ticket(${id})/Action.UpdateCollaborators`, { CollaboratorIds: collaboratorIds });
  };
  UpdateTicketCollaborators = (id: number, collaboratorIds: any) => this.init().post(`UpdateTicketCollaborators`, { TicketId: id, CollaboratorIds: collaboratorIds });

  GetRequestsAndAffectedTickets = (id: number) => {
    return this.init().post(`GetRequestsAndAffectedTickets`, {
      UserPlatformId: id
    });
  };

  createTeam = (data: ITeam) => {
    return this.init().post('Team', data);
  };

  updateTeam = (data: ITeam) => {
    return this.init().patch(`Team(${data.Id})`, data);
  };

  createGroupChat = (ticketId: number, chatUserIds: any) => {
    return this.init().post(`CreateGroupChat`, { TicketId: ticketId, ChatUserIds: chatUserIds });
  };

  getGroupChat = (groupChatId: number) => {
    return this.init().get(`GroupChat(${groupChatId})?$expand=Members`);
  };

  getTicketLifecyclePhases = async (params?: string) => {
    try {
      const result = await this.init().get(`TicketLifecyclePhase${params || ''}`);
      return result.data.value;
    } catch (e) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  };
  deleteTicketLifecycle = async (lifecycleId: number) => {
    try {
      await this.init().delete(`TicketLifecycle(${lifecycleId})`);
    } catch (e) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  }

  getLifecycles = (params?: string) => {
    return this.init().get(`LifeCycle${params || ''}`);
  };

  getLifecyclesSafe = async (params?: string) => {
    try {
      const result = await this.init().get(`LifeCycle${params || ''}`);
      return result.data.value;
    } catch (e) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  };

  getLifecycleTransition = (params?: string) => {
    return this.init().get(`LifecycleTransition${params || ''}`);
  };

  getTransitionRule = (params?: string) => {
    return this.init().get(`TransitionRule${params || ''}`);
  };

  createLifecycle = (data: ILifecycle) => {
    return this.init().post(`LifeCycle`, data);
  };

  saveLifecycle = (data: Lifecycle) => {
    return this.init().post(`SaveLifecycleFlowSetting`, { LifecycleData: JSON.stringify(data) });
  };

  updateLifecycle = (id: number, data: ILifecycle) => {
    return this.init().patch(`LifeCycle(${id})`, data);
  };

  applyLifecycle = (ticketId: number, lifecycleId: number) => {
    return this.init().post(`ApplyLifecycleToTicket`, { TicketId: ticketId, LifecycleId: lifecycleId });
  };

  bulkEditTickets = (ticketIds: number[], changeProps: string, newResolution: string) => {
    return this.init('api/', {}, { timeout: (ticketIds.length > 2) ? ticketIds.length * 20000 : 40000 }).post('BulkEditTickets', { TicketIds: ticketIds, ChangeProperties: changeProps, ResolutionDetails: newResolution });
  };

  addTicketApproval = async (data: Approval) => {
    return this.init().post('Approvals', data);
  };

  getTicketApprovals = async (ticketId: number) => {
    const result = await this.init()
      .get(`ticketapprovals?$filter=ticketid%20eq%20${ticketId}%20&$select=approval &$expand=approval($expand=Approvers($expand=Approver))`)
    return result.data.value;
  };

  getTicketApprovalsOfPhaseId = async (ticketId: number, activePhaseId) => {
    const result = await this.init()
      .get(`ticketapprovals?$filter=ticketid%20eq%20${ticketId}%20and%20approval/TicketLifecyclePhaseId%20eq%20${activePhaseId}&$select=approval &$expand=approval($expand=Approvers($expand=Approver))`)
    return result.data.value;
  };


  updateTicketApprover = async (data: any, id: number) => {
    return this.init().patch(`Approvers(${id})`, data);
  };

  updateTicketApproval = async (data: any, id: number) => {
    return this.init().patch(`Approvals(${id})`, data);
  };

  getApproval = async (id: number) => {
    const result = await this.init().get(`approvals?$filter=Id%20eq%20${id}`);
    return result.data.value;
  }

  getApprovalApprovers = async (id: number) => {
    const result = await this.init().get(`approvals?$expand=Approvers&$filter=Id%20eq%20${id}`);
    return result.data.value
  }

  getApprovalApproversTicketLifecyclePhase = async (id: number) => {
    const result = await this.init().get(`approvals(${id})?$expand=Approvers,TicketLifecyclePhase`);
    return result.data;
  }

  getApprover = async (id: number) => {
    const result = await this.init().get(`approvers(${id})`);
    return result.data;
  }

  getAllApprovals = async () => {
    try {
      const result = await this.init().get(`ticketapprovals`);
      return result.data.value;
    } catch (e: any) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  }

  getApprovalsByUserId = async (platformUserId: number, isLifecyclesEnabled: boolean, ticketId: number = null, showAllApprovals: boolean = false) => {
    try {
      const ticketFilter = ticketId ? `TicketId eq ${ticketId}` : '';
      const approverFilter = showAllApprovals ? "" : `Approval/Approvers/any(a: a/Approver/Id eq ${platformUserId}) and `;
      const phaseFilter = currentPhaseFilter('Approval/', isLifecyclesEnabled);
      const result = await this.init().get(`ticketapprovals?$expand=Ticket($expand=TicketLifecycle($expand=Status,Lifecycle($select=Title),Phases($filter=IsCurrent eq true))),Approval($expand=Approvers($expand=Approver),TicketLifecyclePhase)
      &$filter=${approverFilter}${ticketFilter} and ${phaseFilter} &$orderby=Approval/ModifiedDate desc`)
      return result.data.value;
    } catch (e) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  }
  getApproversByUser = async (userId: number, filter?: string) => {
    const result = await this.init().get(`RequestPortalApprovals${filter ?? ''}`);
    return result.data;
  }

  getAllPendingTicketApprovals = async () => {
    try {
      const result = await this.init().get(`ticketapprovals?$expand=approval($filter=ApprovalState eq 3)`)
      return result.data.value;
    } catch (e) {
      if (e["response"]["status"] === 402) return [];
      throw e;
    }
  }
  getApprovalsByEmail = async (filter: string) => {
    const result = await this.init()
      .get(`ticketapprovals?$expand=Ticket,Approval($expand=Approvers($expand=Approver),TicketLifecyclePhase)&$filter=${filter}&$orderby=CreatedDate desc`)
    return result.data.value;
  };

  getKnowledgeAnswer = (question: string) => this.init().post(`GetKnowledgeAnswer`, { Question: question });
  getKnowledgeArticle = (qnaId: number) => this.init().get(`GetKnowledgeArticle(Id=${qnaId})`);
  getTemplateArticle = (qnaId: number) => this.init().get(`GetTemplateArticle(Id=${qnaId})`);
  isTemplateAccessDenied = (templateId: number) => this.init().get(`IsTemplateAccessDenied(TemplateId=${templateId})`);

  createTicketTask = (data: any) => this.init().post(`TicketTask`, data);

  getTicketTasks = async (params?: string) => {
    const result = await this.init().get(`TicketTask${params || ''}`);
    return result.data.value;
  }

  deleteTicketTask = (params: string) => this.init().delete(`${params}`);

  updateTicketTask = (params: string, data) => this.init().patch(`${params}`, data);

  // Time tracking. UserId is assigned server-side from the caller's identity and
  // ignored if sent, so createTicketTimeEntry only needs TicketId, Minutes and EntryDate.
  // clientDay is the caller's own calendar day, sent as x-client-date, and the server needs it
  // to enforce the current-day restriction: an agent in Tokyo at 8am Monday is still on Sunday
  // in UTC, and once a start time is captured the stored value is a real instant whose UTC date
  // can be the next day either way. Only the client knows which day it is where it is.
  //
  // One header, not two. This used to say "both", left over from an x-client-offset that was
  // removed once the offset started riding on EntryDate itself - see toEntryInstant.
  //
  // Required here even though the header is optional to the server - that tolerance is for
  // callers this file is not, such as a direct OData request.
  //
  // 'api/' is init's default, but it is the first positional parameter and the headers are the
  // second, so it has to be passed to reach them - as at the multipart and bulk-edit calls.
  createTicketTimeEntry = (data: any, clientDay: string) =>
    this.init('api/', { 'x-client-date': clientDay })
      .post(`TicketTimeEntry`, data);

  getTicketTimeEntries = async (params?: string) => {
    const result = await this.init().get(`TicketTimeEntry${params || ''}`);
    return result.data.value;
  }

  updateTicketTimeEntry = (params: string, data, clientDay: string) =>
    this.init('api/', { 'x-client-date': clientDay })
      .patch(`${params}`, data);

  deleteTicketTimeEntry = (params: string) => this.init().delete(`${params}`);

  // Replaces everything one agent logged against a ticket with a single total, in one
  // transaction. The only caller passes Minutes: 0 - the per-agent "delete all" - which
  // would otherwise need N deletes from the client. Editing and removing a single entry
  // do NOT come through here; they are updateTicketTimeEntry and deleteTicketTimeEntry
  // above. A positive Minutes is part of the server contract but unused, and carries a
  // known attribution defect - see the remarks on SetAgentTimeSpent before using it.
  setAgentTimeSpent = (ticketId: number, data: { UserId: number; Minutes: number; EntryDate?: string }) =>
    this.init().post(`Ticket(${ticketId})/Action.SetAgentTimeSpent`, data);

  getTaskStatus = async (params?: string) => {
    try {
      const result = await this.init().get(`TaskStatus${params || ''}`);
      return result.data.value;
    } catch (e) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  };

  checkConsent = async (url: string) => await this.init().get(url);

  subscribeToEmail = async (aadId: string, email: string) => await this.init().post(`EmailSubscription`, { AadObjectId: aadId, EmailAddress: email });
  unsubscribeToEmail = async () => await this.init().post(`DisconnectEmailSubscription`);
  updateEmail = async (setting: any) => await this.init().post(`UpdateEmailSubscription`, { EmailConfig: setting });
  subscribeToEmailTeam = async (teamId: number, aadId: string, email: string) => await this.init().post(`Team(${teamId})/ConnectEmail`, { AadObjectId: aadId, EmailAddress: email });
  unsubscribeToEmailTeam = async (teamId: number) => await this.init().post(`Team(${teamId})/DisconnectEmail`);

  private setBatchBody(body: any[], contentId: number, tagId: number) {
    body.push(`Content-ID: ${contentId++}`);
    body.push(`Accept: application/json;q=0.9, */*;q=0.1`);
    body.push(`Content-Type: application/json;IEEE754Compatible=true`);
    body.push(`OData-Version: 4.0`);
    body.push(`OData-MaxVersion: 4.0`, '');
    body.push(`{"@odata.id":"http://local/api/Tag(${tagId})"}`);
    return contentId;
  }

  getTransitionsByCriteria = async (filter?: string) => {
    const resultCriteria = await this.init().get(`TransitionRuleCriteria?filter=TransitionItemId%20eq%20${filter}%20and%20IsDeleted%20eq%20false`);
    const resultCriteriaData = resultCriteria.data.value;
    const transitionRuleGroupIds = resultCriteriaData.map(obj => obj.TransitionRuleGroupId);

    const transitionGroups = [];
    for await (const resultGroupData of transitionRuleGroupIds.map(id => this.init().get(`TransitionRuleGroup?filter=Id%20eq%20${id}%20and%20IsDeleted%20eq%20false`))) {
      transitionGroups.push(resultGroupData.data.value[0])
    }

    const transitionRules = [];
    for await (const resultRuleData of transitionRuleGroupIds.map(id => this.init().get(`TransitionRule?filter=Id%20eq%20${id}%20and%20IsDeleted%20eq%20false`))) {
      transitionRules.push(resultRuleData.data.value[0])
    }

    const transitionIds = transitionRules.map(obj => obj.TransitionId);

    return await this.getTransitionsById(transitionIds);
  }

  getTransitionsByTransitionAction = async (filter?: string) => {
    const result = await this.init().get(`TransitionAction?$select=TransitionId&$filter=TransitionItemId%20eq%20${filter}%20and%20IsDeleted%20eq%20false`);
    const resultData = result.data.value;
    const transitionIds = resultData.map(obj => obj.TransitionId);

    return await this.getTransitionsById(transitionIds);
  }

  getTransitionsById = async (transitionIds?: any[]) => {
    const list = [];

    for await (const transitionResult of transitionIds.map(id => this.init().get(`LifecycleTransition?filter=Id%20eq%20${id}%20and%20IsDeleted%20eq%20false`))) {
      list.push(transitionResult.data.value[0])
    }

    return list;
  }

  updateTicketTaskCollaborators = (id: number, collaboratorIds: any) => {
    return this.init().post(`TicketTask(${id})/Action.UpdateTaskCollaborators`, { CollaboratorIds: collaboratorIds });
  };

  executeManualPhaseTransition = async (id: number, transitionMessage: any, exitPhaseIndex) => {
    return this.init().post(`TicketLifecyclePhase(${id})/Action.ExecuteManualPhaseTransition`, { TransitionMessage: transitionMessage, ManuallyExitedIndex: exitPhaseIndex });
  }

  updateTicketApprovalApprovers = (id: number, approverIds: any, approvalData: string) => {
    return this.init().post(`Approvals(${id})/Action.UpdateTicketApprovals`, { ApproverIds: approverIds, ApprovalData: approvalData });
  }

  /**
   * The single funnel for every chunked upload in the app, which is why the Teams channel is attached here
   * rather than threaded through the nine components that reach it. The two ids tell the server which
   * channel's library to file the upload in; undefined outside Teams, and the server then resolves the
   * destination from the ticket's team exactly as before.
   */
  getUploadUrl = async (ticketId: number | string, fileName: string, fileSize: number, uploadAsTemporary: boolean = false, cfData: string = "") => {
    let ticket = ticketId;
    if (typeof ticketId === "string")
      ticket = parseInt(ticketId, 10);

    const { teamsTeamId, teamsChannelId } = getTeamsChannelContext();

    return await this.init().post('CreateUploadSession', { TicketId: ticket, FileName: fileName, FileSize: fileSize, UploadAsTemporary: uploadAsTemporary, CustomFormData: cfData, TeamsTeamId: teamsTeamId, TeamsChannelId: teamsChannelId });
  }

  getDataSources = (params?: string) => {
    return this.init().get(`CustomFieldDataSource${params || ''}`);
  };

  getDataSource = (id?: number) => {
    return this.init().get(`CustomFieldDataSource(${id})`);
  };

  createDataSource = (data: any) => this.init().post(`CustomFieldDataSource`, data);
  updateDataSource = (data: any, id: number) => this.init().patch(`CustomFieldDataSource(${id})`, data);
  deleteDataSource = (id: number) => this.init().delete(`CustomFieldDataSource(${id})`);

  getCustomFormOptions = async (property: string) => {
    try {
      const result = await this.init().get(`GetCustomFormOptions(TicketProperty='${property}')`);
      const parsedResult = JSON.parse(result.data.value);
      switch (property) {
        case TagsTypeahead.ElementId:
          return parsedResult;
        case GroupPicker.ElementId:
          return { data: { value: parsedResult } };
        default:
          return parsedResult;
      }
    } catch (e) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  } 
  
  // Service Catalog Requests
  getServiceOfferings = (params?: string) => this.init().get(`ServiceOffering${params || ''}`);
  getServiceOfferingsSafe = async (params?: string) => {
    try {
      const result = await this.init().get(`ServiceOffering${params || ''}`);
      return result.data.value;
    } catch (e) {
      if (e['response']?.status === 402) return [];
      throw e;
    }
  }

  getServiceOfferingDefault = (params?: string) => this.init().get(`ServiceOffering/Action.GetDefaultInstance`);
  createOffering = (data: any) => this.init().post(`ServiceOffering`, data);
  updateOffering = (data: any) => this.init().patch(`ServiceOffering(${data.Id})`, data);
  deleteOffering = (id: number) => this.init().delete(`ServiceOffering(${id})`);
  orderOfferings = (orderedIds: number[]) => this.init().post(`ReorderServiceOffering`, { OrderedIds: orderedIds });
  getServiceCatalog = () => this.init().get(`GetServiceCatalog`);
  resetFoundryIndexCache = () => this.init().get(`ResetFoundryIndexCache`);

  // Custom Node
  getCustomNodes = async () => {
    try {
      return await this.init().get(`CustomNode?$orderby=Order`);
    } catch (e) {
      if (e['response']?.status === 402) return { data: { value: [] } };
      throw e;
    }
  }
  getForms = (params?: string) => {
    return this.init().get(`Form${params || ''}`);
  };

  createForm = (data: any) => {
    return this.init().post(`Form`, data);
  };

  updateForm = (data: any) => {
    return this.init().patch(`Form(${parseInt(data.Id)})`, data);
  };

  deleteForm = (id: number) => {
    return this.init().delete(`Form(${id})`);
  };

  getDefaultTemplateInstance = () => {
    return this.init().get(`TicketTemplate/Action.GetDefaultTicketTemplateInstance`);
  };

  saveTemplateForms = (id: number, templateFormList: any) => {
    const cleanTemplateFormList =  templateFormList.filter(x=>x!=undefined);
    const templateformPayload = cleanTemplateFormList.map(item => ({
      Id: item.Id,
      Name: item.Form.Name,
      Order: item.Order,
      Form: {Id:item.Form.Id, Name:item.Form.Name},
      FormId: item.Form.id,
      IsExpand: false,
      SubmitButtonText: item.SubmitButtonText,
      Transitions: (item.Transitions == null || item.Transitions == undefined) ? [] : item.Transitions.map(transition=>({
        Id: transition.Id,
        Name: transition.Name,
        UseDefaultLogic: transition.UseDefaultLogic,
        UseLogicBuilder: transition.UseLogicBuilder,
        IsExpand: false,
        ActionType: transition.ActionType,
        TransitionFormIndex: transition.TransitionFormIndex,
        TransitionGroups: (transition.TransitionGroups == null || transition.TransitionGroups == undefined) ? [] : transition.TransitionGroups.map(ruleItem=>({
          Id: ruleItem.Id,
          LogicalOperator: ruleItem.LogicalOperator,
          Filters: (ruleItem.Filters == null || ruleItem.Filters == undefined) ? [] : ruleItem.Filters.map(filterItem=>({
            Id: filterItem.Id,
            Property: filterItem.Property,
            PropertyDisplayText: filterItem.PropertyDisplayText,
            Value: filterItem.Value,
            Operator: filterItem.Operator ?? "Equal",
            Type: filterItem.Type
          }))
        }))
      }))
    }));
    const payload = {
      TemplateForms: templateformPayload,
      TemplateId: id
    }
    return this.init().post(`SaveTemplateForms`, payload);
  };

  executeFormTransition = (templateId: any, templateFormId: any, customFormAnswer: any, ticketId: any) => {
    const customFormPayLoad = {
      TemplateFormId: templateFormId,
      FormName: customFormAnswer.FormName,
      Order: customFormAnswer.Order,
      CustomFormInputs: Object.entries(customFormAnswer.CardAnswerJson).map(([k, v]) => ({ question: k, key: k, answer: v }))
    }
    const payload = {
      TemplateId: templateId,
      TemplateFormId: templateFormId,
      TicketId: ticketId,
      CustomFormAnswer: JSON.stringify(customFormPayLoad),
    }

    return this.init().post(`ExecuteFormTransition`, payload);
  };

  updateCustomForms = (id : number, customForms : any) => {
    return this.init().post(`Ticket(${id})/Action.UpdateCustomForms`, { CustomForms: customForms });
  };
  updateTicketCustomForms = (ticketId: number, customForms: any) => this.init().post(`UpdateTicketCustomForms`, { TicketId: ticketId, CustomForms: customForms });

  // Custom Properties
  getCustomProperties = (params?: string) => this.init().get(`CustomProperty${params || ''}`);
  createCustomProperty = (data: any) => this.init().post(`CustomProperty`, data);
  updateCustomProperty = (data: any) => this.init().patch(`CustomProperty(${data.Id})`, data);
  deleteCustomProperty = (id: number) => this.init().delete(`CustomProperty(${id})`);
  orderCustomProperties = (orderedIds: number[]) => this.init().post(`ReorderDraggableItems`, { OrderedIds: orderedIds, EntityName: "CustomProperty" });

  updateCustomPropertyList = (propertyId: number, data: any[], configData: any) => this.init().post(`UpdateCustomPropertyList`, {CustomPropertyId: propertyId, CustomPropertyListItemsDto: data, CustomPropertyListConfiguration: configData} );

  getCustomPropertyListItem = (params?: string) => this.init().get(`CustomPropertyListItem${params || ''}`);
  createCustomPropertyListItem = (data: any) => this.init().post(`CustomPropertyListItem`, data);
  updateCustomPropertyListItem = (data: any) => this.init().patch(`CustomPropertyListItem(${data.Id})`, data);
  deleteCustomPropertyListItem = (id: string | number) => this.init().delete(`CustomPropertyListItem(${id})`);

  getTeamsPayload = (token: string) => this.init().get(`GetTeamsPayload(Token='${token}')`);

  getTeamsNotificationMatrix = (params?: string) => this.init().get(`TeamsNotificationMatrix${params || ''}`);
  getEmailNotificationMatrix = (params?: string) => this.init().get(`EmailNotificationMatrix${params || ''}`);
  saveNotificationMatrix = (data: any) => this.init().post(`SaveNotificationMatrix`, data);
}